import { useEffect, useRef, useState } from "react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import Services from "../services/services";
import Title from "../components/Title";
import { useSelector } from "react-redux";
import { RoutePages } from "../ultils";

function CountdownCircle({ duration = 5000, size = 32, stroke = 4, color = "#3b82f6" }) {
  const [percent, setPercent] = useState(1);
  const startRef = useRef();

  useEffect(() => {
    setPercent(1);
    startRef.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.max(0, 1 - elapsed / duration);
      setPercent(p);
      if (p > 0) {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
    return () => { };
  }, [duration]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      className="mx-2"
      style={{ minWidth: size, minHeight: size }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#e5e7eb"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - percent)}
        strokeLinecap="round"
        style={{
          transition: "stroke-dashoffset 0.1s linear",
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
        }}
      />
    </svg>
  );
}
const FETCH_INTERVAL = 5000; // ms
const APIStatus = () => {
  const authStore = useSelector((state) => state.auth);

  const [chartData, setChartData] = useState([]);
  const [rangeMinutes, setRangeMinutes] = useState(15);
  const [serviceCallback, setServiceCallback] = useState("");
  const [isViewGlobal, setIsViewGlobal] = useState(authStore.user === null);

  const [totalRequests, setTotalRequests] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);
  const [errorRate, setErrorRate] = useState(0.0);
  const [kbytesPerRequest, setKBytesPerRequest] = useState(0.0);

  // State để trigger lại vòng tròn mỗi lần fetch
  const [circleKey, setCircleKey] = useState(0);

  // useEffect cho fetch và countdown
  useEffect(() => {
    const doFetch = async () => {
      await fetchStatistics();
      if (rangeMinutes <= 15) setCircleKey((k) => k + 1); // reset vòng tròn
    };
    doFetch();
    if (rangeMinutes <= 15) {
      const interval = setInterval(() => {
        doFetch();
      }, FETCH_INTERVAL);
      return () => {
        clearInterval(interval);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeMinutes, isViewGlobal, serviceCallback]); 

  useEffect(() => {
    var totalCall = chartData.reduce((sum, d) => sum + (d.total_requests || 0), 0)
    var totalError = chartData.reduce((sum, d) => sum + (d.error_requests || 0), 0);
    setAvgLatency(
      chartData.filter(d => d.avg_latency > 0).length > 0
        ? chartData.reduce((sum, d) => sum + (d.avg_latency || 0), 0) / chartData.filter(d => d.avg_latency > 0).length
        : 0
    );
    setTotalRequests(totalCall);
    setErrorRate(totalCall > 0 ? ((totalError / totalCall) * 100).toFixed(2) : 0);
    const totalKBytes = chartData.reduce(
      (sum, d) => sum + ((d.kbytes_per_request || 0) * (d.total_requests || 0)),
      0
    );
    setKBytesPerRequest(totalCall > 0 ? totalKBytes / totalCall : 0);
  }, [chartData])

  // Fetch request statistics
  const fetchStatistics = async () => {
    try {
      var response;
      if (!isViewGlobal) {
        response = await Services.ServiceUsage.getPersonal(rangeMinutes, serviceCallback);
      }
      else {
        response = await Services.ServiceUsage.getGlobal(rangeMinutes, serviceCallback);
      }
      const data = await response.json();
      if (data.success) {
        const formattedData = Object.keys(data.data).map((minute) => {
          const usage = data.data[minute];
          const total_requests = usage.ok_count + usage.fail_count;
          return {
            minute,
            total_requests: total_requests,
            error_requests: usage.fail_count,
            avg_latency: usage.avg_latency,
            kbytes_per_request: 
              total_requests > 0 
              ? (usage.total_bytes / (1024)) / total_requests 
              : 0,
          };
        });
        setChartData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching log statistics:", error);
    }
  };

  const COLOR_REQUESTS = isViewGlobal ? "#3b82f6" : "#28a745";
  const COLOR_ERRORS = "#dc3545";
  const COLOR_BANDWIDTH = "#30d7e3";
  const COLOR_LATENCY = "#bbbbbb";

  return (
    <div className="p-2 sm:p-5">
      <Title title="API Status" page={RoutePages.API_STATUS} />
      {/* Thống kê tổng hợp */}
      <div className="mb-4 flex flex-wrap gap-4 sm:gap-6 bg-white rounded shadow p-3 sm:p-4 w-full max-w-3xl">
        <div className="flex flex-col gap-1 sm:gap-3 flex-1 min-w-[130px]">
          <div className="text-base text-gray-500">Total API Calls</div>
          <div className="text-2xl font-bold break-all" style={{ color: COLOR_REQUESTS }}>{totalRequests.toLocaleString()}</div>
        </div>
        <div className="flex flex-col gap-1 sm:gap-3 flex-1 min-w-[130px]">
          <div className="text-base text-gray-500">Error Rate</div>
          <div className="text-2xl font-bold" style={{ color: COLOR_ERRORS }}>{errorRate}%</div>
        </div>
        <div className="flex flex-col gap-1 sm:gap-3 flex-1 min-w-[130px]">
          <div className="text-base text-gray-500">KB/request</div>
          <div className="text-2xl font-bold flex" style={{ color: COLOR_BANDWIDTH }}>{kbytesPerRequest.toLocaleString(undefined, { maximumFractionDigits: 0 })} KB</div>
        </div>
        <div className="flex flex-col gap-1 sm:gap-3 flex-1 min-w-[130px]">
          <div className="text-base text-gray-500">Avg Latency</div>
          <div className="text-2xl font-bold flex" style={{ color: COLOR_LATENCY }}>{avgLatency.toLocaleString(undefined, { maximumFractionDigits: 0 })}ms</div>
        </div>
      </div>

      {/* Statistics Chart */}
      <div className="bg-white p-2 sm:p-4 shadow-md rounded-lg">
        <div className="flex flex-col sm:flex-row items-center justify-start mb-4 gap-2 sm:gap-0">
          <div className="flex items-center gap-2 sm:gap-4 ml-0 sm:ml-4">
            <span className={`text-base sm:text-xl text-gray-700 ${isViewGlobal ? "" : "font-bold"}`}>Personal Usages</span>
            <button
              onClick={() => setIsViewGlobal((prev) => !prev)}
              disabled={authStore.user === null}
              className={`w-12 sm:w-16 h-8 rounded-full transition-colors ${isViewGlobal ? "bg-blue-500" : "bg-green-500"} relative ${authStore.user === null && "opacity-50 cursor-not-allowed"}`}
            >
              <span className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${isViewGlobal ? "translate-x-4 sm:translate-x-8" : ""}`}
              ></span>
            </button>
            <span className={`text-base sm:text-xl text-gray-700 ${isViewGlobal ? "font-bold" : ""}`}>Global Usages</span>
          </div>

          <select
            className="ml-0 sm:ml-4 p-2 border-2 rounded-md bg-white mt-2 sm:mt-0"
            value={rangeMinutes}
            onChange={(e) => setRangeMinutes(Number(e.target.value))}
          >
            <option value={15}>In 15 minutes</option>
            <option value={60}>In 1 hour</option>
            <option value={180}>In 3 hours</option>
            <option value={720}>In 12 hours</option>
            <option value={1440}>In 24 hours</option>
            <option value={4320}>In 3 days</option>
            <option value={10080}>In 7 days</option>
            <option value={43200}>In 30 days</option>
            <option value={129600}>In 90 days</option>
          </select>

          <select
            className="ml-0 sm:ml-4 p-2 border-2 rounded-md bg-white mt-2 sm:mt-0"
            value={serviceCallback}
            onChange={(e) => setServiceCallback(e.target.value)}
          >
            <option value={""}>All Services</option>
            <option value={"fb_graph"}>FB Graph</option>
            <option value={"fb_action"}>FB Action</option>
            <option value={"tiktok"}>TikTok</option>
          </select>
          <CountdownCircle key={circleKey} duration={FETCH_INTERVAL} color={isViewGlobal ? "#3b82f6" : "#28a745"} />
        </div>
        <div className="w-full h-[250px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%" style={{ zIndex: 0 }}>
            <ComposedChart data={chartData} isAnimationActive={false}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="minute" />

              <Tooltip
                isAnimationActive={false}
              />

              {/* Y-axis for request count */}
              <YAxis
                yAxisId="y_requests"
                label={{
                  value: "Requests count",
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: isViewGlobal ? "#3b82f6" : "#28a745" }
                }}
                stroke={isViewGlobal ? "#3b82f6" : "#28a745"}
              />

              {/* Y-axis for average latency */}
              <YAxis
                yAxisId="y_avg_latency"
                orientation="right"
                label={{
                  value: "Avg Delay (ms)",
                  angle: 90,
                  position: "insideRight",
                  offset: 80,
                  style: {
                    fill: "#bbbbbb"
                  }
                }}
              />

              {/* Y-axis for total bytes */}
              <YAxis
                yAxisId="y_kbytes_per_request"
                orientation="right"
                label={{
                  value: "KB/request",
                  angle: 90,
                  position: "insideRight",
                  offset: 25,
                  style: { fill: "#30d7e3" }
                }}
                stroke="#30d7e3"
              />

              {/* Line chart for all requests */}
              <Line
                yAxisId="y_requests"
                type="monotone"
                dataKey="total_requests"
                stroke={isViewGlobal ? "#3b82f6" : "#28a745"}
                strokeWidth={2}
                name="Totals"
                unit={" request(s)"}
                formatter={(value) => value.toLocaleString()}
                dot={false}
                isAnimationActive={false}
              />

              {/* Line chart for requests with status_code != 200 */}
              <Line
                yAxisId="y_requests"
                type="monotone"
                dataKey="error_requests"
                stroke="#dc3545"
                strokeWidth={2}
                name="Errors"
                unit={" request(s)"}
                formatter={(value) => value.toLocaleString()}
                dot={false}
                isAnimationActive={false}
              />

              {/* Line chart for kbytes_per_request*/}
              <Line
                yAxisId="y_kbytes_per_request"
                type="monotone"
                dataKey="kbytes_per_request"
                stroke="#30d7e3"
                strokeWidth={1}
                name="Avg Bandwidth"
                unit={" KB/request"}
                formatter={(value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                dot={false}
                isAnimationActive={false}
              />

              {/* Bar chart for average delay (normalized) */}
              <Bar
                yAxisId="y_avg_latency"
                dataKey="avg_latency"
                fill="#bbbbbb"
                name="Avg Latency"
                barSize={3}
                unit={"ms"}
                isAnimationActive={false}
                formatter={(value) => value.toLocaleString()}
              />

              {/* Bar chart for total bytes */}
              {/* <Bar
                yAxisId="y_total_bytes"
                dataKey="total_kbytes"
                fill="#30d7e3"
                name="Bandwidth"
                barSize={3}
                unit={" KB"}
                isAnimationActive={false}
              /> */}

            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default APIStatus;
